/**
 * Location Hooks
 * ==============
 * React Query hooks for location operations.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { locationApi } from '@/services/api'
import type { LocationCreate, LocationUpdate } from '@/types'

// Query keys
export const locationKeys = {
  all: ['locations'] as const,
  lists: () => [...locationKeys.all, 'list'] as const,
  list: (includeCounts?: boolean) => [...locationKeys.lists(), { includeCounts }] as const,
  details: () => [...locationKeys.all, 'detail'] as const,
  detail: (id: number) => [...locationKeys.details(), id] as const,
}

/**
 * Hook to fetch all locations.
 */
export function useLocations(includeCounts = false) {
  return useQuery({
    queryKey: locationKeys.list(includeCounts),
    queryFn: () => locationApi.list(includeCounts),
  })
}

/**
 * Hook to fetch a single location.
 */
export function useLocation(id: number) {
  return useQuery({
    queryKey: locationKeys.detail(id),
    queryFn: () => locationApi.get(id),
    enabled: !!id,
  })
}

/**
 * Hook to create a new location.
 */
export function useCreateLocation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: LocationCreate) => locationApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: locationKeys.lists() })
    },
  })
}

/**
 * Hook to update a location.
 */
export function useUpdateLocation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: LocationUpdate }) =>
      locationApi.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: locationKeys.lists() })
      queryClient.invalidateQueries({ queryKey: locationKeys.detail(id) })
    },
  })
}

/**
 * Hook to delete a location.
 */
export function useDeleteLocation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, reassignTo }: { id: number; reassignTo?: number }) =>
      locationApi.delete(id, reassignTo),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: locationKeys.lists() })
    },
  })
}
